import { getObjectTypedKeys, kebabCaseToCamelCase } from '@augment-vir/common';
import { css } from 'lit';
import { property } from 'lit/decorators.js';
import { DeclarativeElementMarkerSymbol } from '../declarative-element-marker-symbol';
import { DeclarativeElement, } from './declarative-element';
import { defaultDeclarativeElementDefinitionOptions, IgnoreInputsNotBeenSetBeforeRenderWarningSymbol, } from './definition-options';
import { assign } from './directives/assign.directive';
import { hasDeclarativeElementParent } from './has-declarative-element-parent';
import { toAsyncStateHandlerMap } from './properties/async-state';
import { createCssVarNamesMap, createCssVarValuesMap } from './properties/css-vars';
import { createEventDescriptorMap } from './properties/element-events';
import { createElementUpdaterProxy } from './properties/element-updater-proxy';
import { createHostClassNamesMap } from './properties/host-classes';
import { applyHostClasses, hostClassNamesToStylesInput } from './properties/styles';
import { createRenderParams } from './render-callback';
export function defineElementNoInputs(initInput) {
    var _a;
    const eventsMap = createEventDescriptorMap(initInput.events);
    const hostClassNames = createHostClassNamesMap(initInput.tagName, initInput.hostClasses);
    const cssVarNames = createCssVarNamesMap(initInput.tagName, initInput.cssVars);
    const cssVarValues = createCssVarValuesMap(initInput.cssVars, cssVarNames);
    const elementOptions = {
        ...defaultDeclarativeElementDefinitionOptions,
        ...initInput.options,
    };
    const calculatedStyles = typeof initInput.styles === 'function'
        ? initInput.styles(hostClassNamesToStylesInput({ hostClassNames, cssVarNames, cssVarValues }))
        : initInput.styles || css ``;
    const typedRenderCallback = initInput.renderCallback;
    const anonymousClass = (_a = class extends DeclarativeElement {
            createRenderParams() {
                return createRenderParams(this, eventsMap);
            }
            get instanceType() {
                throw new Error(`"instanceType" was called on ${initInput.tagName} as a value but it is only for types.`);
            }
            static get inputsType() {
                throw new Error(`"inputsType" was called on ${initInput.tagName} as a value but it is only for types.`);
            }
            static get stateType() {
                throw new Error(`"stateType" was called on ${initInput.tagName} as a value but it is only for types.`);
            }
            markInputsAsHavingBeenSet() {
                if (!this.haveInputsBeenSet) {
                    this.haveInputsBeenSet = true;
                }
            }
            render() {
                if (
                // This ignores elements at the root of a page, as they can't receive inputs from
                // other elements (cause they have no custom element ancestors).
                hasDeclarativeElementParent(this) &&
                    !this.haveInputsBeenSet &&
                    !elementOptions[IgnoreInputsNotBeenSetBeforeRenderWarningSymbol]) {
                    console.warn(this, `${initInput.tagName} got rendered before its input object was set. This was most likely caused by forgetting to use the "${assign.name}" directive on it. If no inputs are intended, use "${defineElementNoInputs.name}" to define ${initInput.tagName}.`);
                }
                const renderParams = this.createRenderParams();
                if (!this.initCalled && initInput.initCallback) {
                    this.initCalled = true;
                    initInput.initCallback(renderParams);
                }
                const renderResult = initInput.renderCallback(renderParams);
                applyHostClasses({
                    host: renderParams.host,
                    hostClassesInit: initInput.hostClasses,
                    hostClassNames,
                    state: renderParams.state,
                    inputs: renderParams.inputs,
                });
                return renderResult;
            }
            disconnectedCallback() {
                super.disconnectedCallback();
                if (initInput.cleanupCallback) {
                    const renderParams = this.createRenderParams();
                    initInput.cleanupCallback(renderParams);
                }
                this.initCalled = false;
            }
            assignInputs(inputs) {
                getObjectTypedKeys(inputs).forEach((key) => {
                    property()(this, key);
                    this.instanceInputs[key] = inputs[key];
                });
                this.markInputsAsHavingBeenSet();
            }
            constructor() {
                super();
                this.initCalled = false;
                this.haveInputsBeenSet = false;
                // this is set below in Object.defineProperties
                this.definition = {};
                this.asyncStateHandlerMap = toAsyncStateHandlerMap(initInput.stateInit);
                this.instanceInputs = createElementUpdaterProxy(this, false);
                this.instanceState = createElementUpdaterProxy(this, true);
                const stateInit = initInput.stateInit ||
                    {};
                getObjectTypedKeys(stateInit).forEach((stateKey) => {
                    property()(this, stateKey);
                    const asyncStateClassInstance = this.asyncStateHandlerMap[stateKey];
                    if (asyncStateClassInstance) {
                        this.instanceState[stateKey] = asyncStateClassInstance.getValue();
                        asyncStateClassInstance.addSettleListener(() => {
                            this[stateKey] =
                                asyncStateClassInstance.getValue();
                        });
                    }
                    else {
                        this.instanceState[stateKey] = stateInit[stateKey];
                    }
                });
                this.definition = anonymousClass;
            }
        },
        _a.tagName = initInput.tagName,
        _a.styles = calculatedStyles,
        // this gets set below in Object.defineProperties
        _a.isStrictInstance = () => false,
        _a.events = eventsMap,
        _a.renderCallback = typedRenderCallback,
        _a.hostClasses = hostClassNames,
        _a.cssVarNames = cssVarNames,
        _a.stateInit = initInput.stateInit,
        _a.cssVarValues = cssVarNames,
        _a);
    Object.defineProperties(anonymousClass, {
        [DeclarativeElementMarkerSymbol]: {
            value: true,
            writable: false,
        },
        name: {
            value: kebabCaseToCamelCase(initInput.tagName, {
                capitalizeFirstLetter: true,
            }),
            writable: true,
        },
        isStrictInstance: {
            value: (element) => {
                return element instanceof anonymousClass;
            },
            writable: false,
        },
    });
    if (window.customElements.get(initInput.tagName)) {
        console.warn(`Tried to define custom element '${initInput.tagName}' but it is already defined.`);
    }
    else {
        window.customElements.define(initInput.tagName, anonymousClass);
    }
    return anonymousClass;
}
